import React, { useEffect, useRef, useState } from "react";
import { Camera, Image as ImageIcon, LayoutGrid, Loader2, RotateCw, ZoomIn, ZoomOut, X } from "lucide-react";
import { toast } from "sonner";
import client, { resolveImageUrl, updateProfile } from "@/lib/api";
import { ImageLibraryDialog } from "@/components/ImageLibrary";
import { Slider } from "@/components/ui/slider";
import { isImageFile, uploadImageFile, uploadErrorText } from "@/lib/imageUpload";

const V = 260; // editor viewport (px)
const OUT = 512; // saved avatar size (px)
const btn = "inline-flex items-center gap-1 rounded-full border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 px-2.5 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-60";

const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
const rotDims = (img, rot) => (rot % 180 ? [img.naturalHeight, img.naturalWidth] : [img.naturalWidth, img.naturalHeight]);
const scaleFor = (img, rot, zoom, size) => { const [rw, rh] = rotDims(img, rot); return (size / Math.min(rw, rh)) * zoom; };
const clampOff = (img, rot, zoom, o) => {
  const [rw, rh] = rotDims(img, rot); const s = scaleFor(img, rot, zoom, V);
  const mx = Math.max(0, (rw * s - V) / 2), my = Math.max(0, (rh * s - V) / 2);
  return { x: Math.min(mx, Math.max(-mx, o.x)), y: Math.min(my, Math.max(-my, o.y)) };
};

// Renders the image with the current crop transform at any square size (editor + live preview).
function Stage({ img, src, rot, zoom, off, size }) {
  const k = size / V;
  const s = scaleFor(img, rot, zoom, size);
  return (
    <img src={src} alt="" draggable={false} className="absolute left-1/2 top-1/2 max-w-none select-none pointer-events-none"
      style={{ width: img.naturalWidth, height: img.naturalHeight, transform: `translate(-50%,-50%) translate(${off.x * k}px,${off.y * k}px) rotate(${rot}deg) scale(${s})` }} />
  );
}

function EditorModal({ src, img, onCancel, onSave, saving }) {
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const down = (e) => { e.currentTarget.setPointerCapture(e.pointerId); drag.current = { x: e.clientX, y: e.clientY, o: off }; };
  const move = (e) => { const d = drag.current; if (!d) return; setOff(clampOff(img, rot, zoom, { x: d.o.x + e.clientX - d.x, y: d.o.y + e.clientY - d.y })); };
  const up = () => { drag.current = null; };
  const setZ = (z) => { setZoom(z); setOff((o) => clampOff(img, rot, z, o)); };
  const rotate = () => { const r = (rot + 90) % 360; setRot(r); setOff((o) => clampOff(img, r, zoom, o)); };
  const render = () => {
    const c = document.createElement("canvas"); c.width = OUT; c.height = OUT;
    const ctx = c.getContext("2d"); const k = OUT / V;
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, OUT, OUT);
    ctx.translate(OUT / 2 + off.x * k, OUT / 2 + off.y * k); ctx.rotate((rot * Math.PI) / 180);
    const s = scaleFor(img, rot, zoom, OUT); ctx.scale(s, s);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    return new Promise((res) => c.toBlob(res, "image/jpeg", 0.9));
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 bg-slate-900/60 backdrop-blur-sm" data-testid="avatar-editor">
      <div className="bg-white rounded-2xl w-full max-w-sm p-4 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-lg font-bold">Edit profile photo</h4>
          <button type="button" onClick={onCancel} disabled={saving} className="p-2 rounded-full hover:bg-slate-100" aria-label="Cancel" data-testid="avatar-editor-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="relative mx-auto overflow-hidden rounded-xl bg-slate-900 touch-none cursor-grab active:cursor-grabbing" style={{ width: V, height: V }}
          onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="avatar-editor-stage">
          <Stage img={img} src={src} rot={rot} zoom={zoom} off={off} size={V} />
          <div className="absolute inset-0 pointer-events-none rounded-full" style={{ boxShadow: "0 0 0 999px rgba(15,23,42,0.55)" }} />
        </div>
        <p className="text-[11px] text-slate-500 text-center mt-1.5">Drag to reposition</p>
        <div className="flex items-center gap-2 mt-3">
          <ZoomOut className="w-4 h-4 text-slate-500" />
          <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={([z]) => setZ(z)} className="flex-1" data-testid="avatar-editor-zoom" />
          <ZoomIn className="w-4 h-4 text-slate-500" />
          <button type="button" onClick={rotate} className={btn} data-testid="avatar-editor-rotate"><RotateCw className="w-3.5 h-3.5" /> Rotate</button>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <div className="relative w-16 h-16 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0" data-testid="avatar-editor-preview">
            <Stage img={img} src={src} rot={rot} zoom={zoom} off={off} size={64} />
          </div>
          <div className="text-xs text-slate-500">Preview of your final photo</div>
        </div>
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={onCancel} disabled={saving} className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60" data-testid="avatar-editor-cancel">Cancel</button>
          <button type="button" onClick={() => onSave(render)} disabled={saving} className="flex-1 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-70" data-testid="avatar-editor-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save photo"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Customer profile photo: pick (Library / Camera / Gallery) → edit → upload → saved to profile immediately.
export default function AvatarPhotoEditor({ value, onSaved }) {
  const [edit, setEdit] = useState(null); // { src, img, revoke }
  const [libOpen, setLibOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingImg, setLoadingImg] = useState(false);
  const savingRef = useRef(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  useEffect(() => () => edit?.revoke && URL.revokeObjectURL(edit.src), [edit]);

  const openBlob = async (blob) => {
    const src = URL.createObjectURL(blob);
    try { setEdit({ src, img: await loadImg(src), revoke: true }); }
    catch { URL.revokeObjectURL(src); toast.error("This image format is not supported. Please use a JPG, PNG or WEBP photo."); }
  };
  const pick = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!isImageFile(f)) { toast.error("Please choose a JPG, PNG or WEBP image."); return; }
    openBlob(f);
  };
  const fromLibrary = async (url) => {
    setLoadingImg(true);
    try { const r = await client.get(url.replace(/^\/api/, ""), { responseType: "blob" }); await openBlob(r.data); }
    catch { toast.error("Could not load the library image. Please try again."); }
    finally { setLoadingImg(false); }
  };
  const save = async (render) => {
    if (savingRef.current) return;
    savingRef.current = true; setSaving(true);
    try {
      const blob = await render();
      if (!blob) throw new Error("Could not process this photo.");
      const { url } = await uploadImageFile(new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      await updateProfile({ avatar_url: url });
      toast.success("Profile photo saved");
      setEdit(null);
      onSaved?.(url);
    } catch (e) {
      toast.error(`Photo not saved: ${uploadErrorText(e)}`);
    } finally { savingRef.current = false; setSaving(false); }
  };

  const off = saving || loadingImg;
  return (
    <div data-testid="edit-avatar-wrap">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Profile photo (optional)</label>
      <div className="mt-2 flex items-center gap-3">
        <div className="w-20 h-20 rounded-full overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center text-slate-400 flex-shrink-0">
          {loadingImg ? <Loader2 className="w-5 h-5 animate-spin" /> : value ? <img src={resolveImageUrl(value)} alt="" className="w-full h-full object-cover" data-testid="edit-avatar-preview" /> : <ImageIcon className="w-5 h-5" />}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setLibOpen(true)} disabled={off} className={`${btn} border-amber-200 text-amber-800 hover:bg-amber-50`} data-testid="edit-avatar-library-btn"><LayoutGrid className="w-3.5 h-3.5" /> VerB Library</button>
          <button type="button" onClick={() => camRef.current?.click()} disabled={off} className={btn} data-testid="edit-avatar-camera-btn"><Camera className="w-3.5 h-3.5" /> Camera</button>
          <button type="button" onClick={() => galRef.current?.click()} disabled={off} className={btn} data-testid="edit-avatar-gallery-btn"><ImageIcon className="w-3.5 h-3.5" /> Gallery</button>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-1">Photo is saved as soon as you tap “Save photo”. If the camera does not open, allow camera access or use Gallery.</p>
      <input ref={camRef} type="file" accept="image/*" capture="user" className="hidden" onChange={pick} data-testid="edit-avatar-camera-input" />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="edit-avatar-gallery-input" />
      <ImageLibraryDialog open={libOpen} onOpenChange={setLibOpen} initialCategory="shops" onSelect={fromLibrary} />
      {edit && <EditorModal src={edit.src} img={edit.img} saving={saving} onCancel={() => !saving && setEdit(null)} onSave={save} />}
    </div>
  );
}
